import React, { useMemo, useState, useEffect } from "react";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { X } from "lucide-react";
import { LANGUAGES, LANGUAGE_PROFICIENCY_LEVELS } from "@/data/constants";
import { FormSectionProps } from "./types";
import { LanguageSelector } from "../LanguageSelector";

const RoleExperienceSection: React.FC<FormSectionProps> = ({
  register,
  setValue,
  watchedFields,
  errors,
}) => {
  // Memoize languages to avoid dependency issues
  const languages = useMemo(() => watchedFields.languages || [], [watchedFields.languages]);

  // Extract selected language IDs for dependency tracking
  const selectedLanguageIds = useMemo(() => {
    return languages.map((lang) => 
      typeof lang === 'string' ? lang : lang.languageId
    );
  }, [languages]);

  // Memoize available languages (filter out selected ones)
  const availableLanguages = useMemo(() => {
    return LANGUAGES.filter((language) => !selectedLanguageIds.includes(language.id));
  }, [selectedLanguageIds]);

  // Local state for language selector to allow reset after selection
  const [languageSelectorValue, setLanguageSelectorValue] = useState<string>("");

  // Reset selector value when languages change (after adding)
  useEffect(() => {
    setLanguageSelectorValue("");
  }, [languages]);

  const handleLanguageAdd = (languageId: string) => {
    const current = languages || [];
    // Add language with default proficiency of 'intermediate'
    setValue("languages", [...current, { languageId, proficiency: 'intermediate' }]);
    // Reset selector value to close dropdown and show placeholder
    setLanguageSelectorValue("");
  };

  const handleLanguageRemove = (languageId: string) => {
    const current = languages || [];
    setValue("languages", current.filter((item) => {
      const id = typeof item === 'string' ? item : item.languageId;
      return id !== languageId;
    }));
  };

  const handleProficiencyChange = (languageId: string, proficiency: 'beginner' | 'intermediate' | 'advanced' | 'native') => {
    const current = languages || [];
    setValue("languages", current.map((item) => {
      const id = typeof item === 'string' ? item : item.languageId;
      if (id === languageId) {
        return { languageId, proficiency };
      }
      return typeof item === 'string' ? { languageId: item, proficiency: 'intermediate' } : item;
    }));
  };

  return (
    <div className="flex flex-col gap-2">
      <h3 className="text-base">Role & Experience</h3>
      <div className="space-y-4">
        <div className="space-y-2" data-question-field="portfolioLink">
          <Label htmlFor="portfolioLink">
            Portfolio Link <span className="text-red-500">*</span>
          </Label>
          <Input
            id="portfolioLink"
            type="url"
            {...register("portfolioLink")}
            placeholder="https://myportfolio.com"
            className={errors.portfolioLink ? "border-red-500" : ""}
          />
          {errors.portfolioLink && (
            <p className="text-sm text-red-500">{errors.portfolioLink.message}</p>
          )}
          <p className="text-xs text-gray-500">
            Product design portfolios on personal domains, Framer or Webflow get 5x more callbacks.
          </p>
        </div>

        <div className="space-y-2" data-question-field="portfolioPassword">
          <Label>
            Does your portfolio require a password? <span className="text-red-500">*</span>
          </Label>
          <RadioGroup
            value={watchedFields.portfolioPassword}
            onValueChange={(value) => setValue("portfolioPassword", value as "yes" | "no")}
            className="space-y-1 text-neutral-500"
          >
            <div className="flex items-center space-x-4">
              <RadioGroupItem value="yes" id="password-yes" />
              <Label htmlFor="password-yes" className="font-normal cursor-pointer">
                Yes
              </Label>
            </div>
            <div className="flex items-center space-x-4">
              <RadioGroupItem value="no" id="password-no" />
              <Label htmlFor="password-no" className="font-normal cursor-pointer">
                No
              </Label>
            </div>
          </RadioGroup>
          {errors.portfolioPassword && (
            <p className="text-sm text-red-500">{errors.portfolioPassword.message}</p>
          )}
          {watchedFields.portfolioPassword === "yes" && (
            <div className="space-y-2 mt-4" data-question-field="portfolioPasswordValue">
              <Label htmlFor="portfolioPasswordValue">
                Portfolio Password <span className="text-red-500">*</span>
              </Label>
              <Input
                id="portfolioPasswordValue"
                type="password"
                {...register("portfolioPasswordValue")}
                placeholder="Enter portfolio password"
                className={errors.portfolioPasswordValue ? "border-red-500" : ""}
              />
              <p className="text-sm text-gray-500">
                This password won't be shared with anyone. It's only used to view your portfolio.
              </p>
              {errors.portfolioPasswordValue && (
                <p className="text-sm text-red-500">{errors.portfolioPasswordValue.message}</p>
              )}
            </div>
          )}
        </div>

        <div className="space-y-2" data-question-field="linkedInProfile">
          <Label htmlFor="linkedInProfile">
            LinkedIn Profile <span className="text-red-500">*</span>
          </Label>
          <Input
            id="linkedInProfile"
            type="url"
            {...register("linkedInProfile")}
            placeholder="https://linkedin.com/in/yourprofile"
            className={errors.linkedInProfile ? "border-red-500" : ""}
          />
          {errors.linkedInProfile && (
            <p className="text-sm text-red-500">{errors.linkedInProfile.message}</p>
          )}
        </div>

        <div className="space-y-2" data-question-field="currentRoleLevel">
          <Label>
            What is your current role level? <span className="text-red-500">*</span>
          </Label>
          <RadioGroup
            value={watchedFields.currentRoleLevel}
            onValueChange={(value) =>
              setValue("currentRoleLevel", value as "junior" | "mid" | "senior" | "staff" | "principal")
            }
            className="space-y-1 text-neutral-500"
          >
            <div className="flex items-center space-x-4">
              <RadioGroupItem value="junior" id="current-junior" />
              <Label htmlFor="current-junior" className="font-normal cursor-pointer">
                Junior
              </Label>
            </div>
            <div className="flex items-center space-x-4">
              <RadioGroupItem value="mid" id="current-mid" />
              <Label htmlFor="current-mid" className="font-normal cursor-pointer">
                Mid
              </Label>
            </div>
            <div className="flex items-center space-x-4">
              <RadioGroupItem value="senior" id="current-senior" />
              <Label htmlFor="current-senior" className="font-normal cursor-pointer">
                Senior
              </Label>
            </div>
            <div className="flex items-center space-x-4">
              <RadioGroupItem value="staff" id="current-staff" />
              <Label htmlFor="current-staff" className="font-normal cursor-pointer">
                Staff
              </Label>
            </div>
            <div className="flex items-center space-x-4">
              <RadioGroupItem value="principal" id="current-principal" />
              <Label htmlFor="current-principal" className="font-normal cursor-pointer">
                Principal
              </Label>
            </div>
          </RadioGroup>
          {errors.currentRoleLevel && (
            <p className="text-sm text-red-500">{errors.currentRoleLevel.message}</p>
          )}
        </div>

        <div className="space-y-2" data-question-field="languages">
          <Label>
            Which Languages do you speak? <span className="text-red-500">*</span>
          </Label>
          <div className="space-y-3">
            <LanguageSelector
              value={languageSelectorValue}
              onChange={(value) => {
                if (value) {
                  handleLanguageAdd(value);
                }
              }}
              languageList={availableLanguages}
              placeholder="Select Language"
            />
            {languages && languages.length > 0 && (
              <div className="grid md:grid-cols-3 gap-2">
                {languages.map((langItem, index) => {
                  const languageId = typeof langItem === 'string' ? langItem : langItem.languageId;
                  const proficiency = typeof langItem === 'string' ? 'intermediate' : langItem.proficiency;
                  const language = LANGUAGES.find((lang) => lang.id === languageId);
                  if (!language) return null;
                  return (
                    <div
                      key={`${languageId}-${index}`}
                      className="flex justify-between md:justify-start items-center gap-2 p-2 border border-gray-200 rounded-xl bg-gray-50"
                    >
                      <div className="flex gap-1 items-center">
                        <button
                        type="button"
                        onClick={() => handleLanguageRemove(languageId)}
                        className="ml-1 rounded-full hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-offset-1 p-1"
                        aria-label={`Remove ${language.label}`}
                      >
                        <X className="h-3 w-3" />
                      </button>
                      <div className="min-w-0">
                        <div className="text-xs font-medium">{language.label}</div>
                      </div>
                      </div>
                      <Select
                        value={proficiency}
                        onValueChange={(value) => 
                          handleProficiencyChange(languageId, value as 'beginner' | 'intermediate' | 'advanced' | 'native')
                        }
                      >
                        <SelectTrigger className="w-[120px] h-8 text-xs rounded-full bg-slate-100">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="rounded-2xl p-1">
                          {LANGUAGE_PROFICIENCY_LEVELS.map((level) => (
                            <SelectItem 
                              className="text-xs rounded-full py-2 pl-8 pr-3 hover:bg-slate-100 hover:text-slate-900 focus:bg-slate-100 focus:text-slate-900 data-[highlighted]:bg-slate-100 data-[highlighted]:text-slate-900 data-[state=checked]:bg-slate-200 data-[state=checked]:text-slate-900 transition-colors duration-200 ease-in-out" 
                              key={level.id} 
                              value={level.id}
                            >
                              {level.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      
                    </div>
                  );
                })}
              </div>
            )}
          </div>
          {errors.languages && (
            <p className="text-sm text-red-500">{errors.languages.message}</p>
          )}
        </div>
      </div>
    </div>
  );
};

export default RoleExperienceSection;

